import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Rocket, Trash2, ArrowUpRight, Cpu, DollarSign, Pencil, Sparkles } from "lucide-react";
import AppNav from "@/components/AppNav";
import { STARTERS } from "@/pages/starters";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const statusColor = {
  draft: "text-zinc-400 border-zinc-700",
  generated: "text-blue-400 border-blue-500/40",
  deployed: "text-green-400 border-green-500/40",
};

/**
 * A project name taken from the brief.
 *
 * Just enough to tell two projects apart in the grid — the model names the
 * product properly inside the app, and the pencil next to every card renames
 * it. Stopping to ask for a name before anything exists is what this avoids.
 */
export function nameFromBrief(brief) {
  const cleaned = brief
    .replace(/\s+/g, " ")
    .replace(/^(build|make|create|design|generate)\s+(me\s+)?(an?\s+|the\s+)?/i, "")
    .trim();
  if (cleaned === "") return "Untitled project";

  const words = cleaned
    .split(" ")
    .slice(0, 6)
    .join(" ")
    // Truncating mid-phrase strands a dangling word: "A landing page for
    // Meridian, a". Drop trailing joining words and punctuation.
    .replace(/[\s,;:.!?]+$/, "")
    .replace(/\s+(a|an|the|and|or|with|for|in|of|to|on|at|by|from)$/i, "")
    .replace(/[\s,;:.!?]+$/, "");
  if (words === "") return "Untitled project";

  const short = words.length > 60 ? `${words.slice(0, 57).trimEnd()}…` : words;
  return short.charAt(0).toUpperCase() + short.slice(1);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [cost, setCost] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(null); // {id, name}
  const [idea, setIdea] = useState("");
  const [building, setBuilding] = useState(false);

  /**
   * A page, or a real Vite and React project that gets compiled.
   *
   * Remembered, because somebody who builds apps builds apps — asking again on
   * every visit is a question with the same answer every time.
   */
  const [target, setTarget] = useState(
    () => localStorage.getItem("catalina_target") || "site",
  );
  const ideaRef = useRef(null);

  const load = () => {
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/costs/summary").then(({ data }) => setCost(data));
  };
  useEffect(load, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name, description: desc });
      setOpen(false);
      setName(""); setDesc("");
      navigate(`/builder/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  /**
   * Builds straight from a description.
   *
   * The only route in used to be: name the project, write a description, open
   * the builder, then write the prompt. That asks people to name a thing
   * before they have decided what it is. The name is taken from the brief and
   * can be changed later; the brief itself goes to the builder in router
   * state, which starts generating on arrival.
   */
  const build = async () => {
    const brief = idea.trim();
    if (!brief || building) return;

    setBuilding(true);
    try {
      const { data } = await api.post("/projects", {
        name: nameFromBrief(brief),
        description: brief.slice(0, 280),
      });
      navigate(`/builder/${data.id}`, { state: { initialPrompt: brief, initialTarget: target } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuilding(false);
    }
  };

  const openRename = (p, e) => {
    e.stopPropagation();
    setRenaming({ id: p.id, name: p.name, description: p.description || "" });
  };

  const saveRename = async () => {
    const val = (renaming?.name || "").trim();
    if (!val) return;
    try {
      await api.put(`/projects/${renaming.id}`, { name: val, description: renaming.description || "" });
      setRenaming(null);
      toast.success("Project updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Build from a description — the primary way in. */}
        <section className="mb-12">
          <h1 className="font-app text-3xl font-semibold tracking-tight mb-1">
            What do you want to build?
          </h1>
          <p className="text-sm text-muted-foreground mb-5">
            Describe it in plain language. The more specific the brief, the better the app.
          </p>

          <div className="border border-border rounded-xl bg-card focus-within:border-zinc-500 transition-colors">
            <Textarea
              ref={ideaRef}
              data-testid="idea-input"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) build();
              }}
              placeholder="A booking site for a dog groomer in Bristol, with services, prices and an availability calendar…"
              aria-label="Describe the app you want"
              className="min-h-[104px] bg-transparent border-0 resize-none focus-visible:ring-0 text-base"
            />
            <div className="flex items-center justify-between gap-3 px-3 pb-3">
              {/*
                A page, or a real project that gets compiled.

                Here rather than only inside the builder, because this is the
                moment somebody says what they want — and the first build is
                the one that decides what the project IS. Offering the choice
                afterwards is offering it too late.
              */}
              <div className="flex items-center gap-1 p-0.5 rounded-md bg-zinc-900/60 border border-border">
                {[
                  { id: "site", label: "Site", hint: "One page, or a few. No build step — ready in about a minute." },
                  { id: "app", label: "App", hint: "A real Vite and React project with components and state, compiled before you see it. Slower, and a codebase you can grow." },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={`target-${option.id}`}
                    title={option.hint}
                    onClick={() => {
                      setTarget(option.id);
                      localStorage.setItem("catalina_target", option.id);
                    }}
                    disabled={building}
                    className={`rounded px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors disabled:opacity-50 ${
                      target === option.id ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="text-[11px] font-mono text-zinc-600 hidden sm:block">⌘↵ to build</span>
              <Button
                data-testid="build-btn"
                onClick={build}
                disabled={!idea.trim() || building}
                className="bg-white text-black hover:bg-zinc-200 ml-auto"
              >
                {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Build</>}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {STARTERS.map((starter) => (
              <button
                key={starter.id}
                data-testid={`starter-${starter.id}`}
                onClick={() => {
                  setIdea(starter.prompt);
                  ideaRef.current?.focus();
                }}
                className="px-3 py-1.5 rounded-full border border-border text-xs text-muted-foreground
                           hover:text-white hover:border-zinc-600 focus-visible:outline
                           focus-visible:outline-2 focus-visible:outline-kinetic transition-colors"
              >
                {starter.label}
              </button>
            ))}
          </div>
        </section>

        {/* stats */}
        <div className="grid sm:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden mb-10">
          <Stat icon={Rocket} label="Projects" value={projects?.length ?? "—"} />
          <Stat icon={Cpu} label="Generations" value={cost?.generations ?? "—"} />
          <Stat icon={DollarSign} label="Total spend" value={cost ? `$${cost.total_cost.toFixed(4)}` : "—"} mono />
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-app text-2xl font-semibold">Your projects</h1>
            <p className="text-sm text-muted-foreground">Prompt an app into existence.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="bg-white text-black hover:bg-zinc-200">
                <Plus className="w-4 h-4 mr-1" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-app">Create a project</DialogTitle>
                <DialogDescription className="text-muted-foreground">Name your app and open the builder to generate it.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Florist CRM" className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Description (optional)</Label>
                  <Textarea data-testid="project-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)}
                    placeholder="A CRM to manage florist orders and clients" className="bg-background border-border" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="create-project-submit" onClick={create} disabled={creating}
                  className="bg-white text-black hover:bg-zinc-200">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & open builder"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {projects === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl py-20 text-center">
            <p className="text-muted-foreground mb-4">No projects yet.</p>
            <Button onClick={() => setOpen(true)} variant="outline" className="border-border">
              <Plus className="w-4 h-4 mr-1" /> Create your first
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/builder/${p.id}`)}
                className="group border border-border rounded-xl bg-card p-5 cursor-pointer hover:border-zinc-600 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-app font-semibold text-lg group-hover:text-white">{p.name}</h3>
                  <div className="flex items-center gap-2">
                    <button data-testid={`rename-project-${p.id}`} onClick={(e) => openRename(p, e)}
                      className="text-zinc-600 hover:text-white transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-project-${p.id}`} onClick={(e) => remove(p.id, e)}
                      className="text-zinc-600 hover:text-kinetic transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 h-10">{p.description || "No description"}</p>
                <div className="flex items-center justify-between mt-4">
                  <Badge variant="outline" className={`font-mono text-[10px] uppercase ${statusColor[p.status] || statusColor.draft}`}>{p.status}</Badge>
                  {p.deploy_url && <ArrowUpRight className="w-4 h-4 text-green-400" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-app">Edit project</DialogTitle>
            <DialogDescription className="text-muted-foreground">Update the name and description.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input data-testid="rename-input" autoFocus value={renaming?.name || ""}
                onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
                className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Textarea data-testid="edit-desc-input" value={renaming?.description || ""}
                onChange={(e) => setRenaming((r) => ({ ...r, description: e.target.value }))}
                placeholder="What is this project?" className="bg-background border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="rename-submit" onClick={saveRename} className="bg-white text-black hover:bg-zinc-200">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono }) {
  return (
    <div className="bg-card p-6">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" /><span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-3xl font-semibold ${mono ? "font-mono text-kinetic" : "font-app"}`}>{value}</p>
    </div>
  );
}
